/**
 * dossierExport — Orchestrates the DIAN ZIP dossier export.
 *
 * Pipeline:
 *  1. Fetch embarque + docs + inventory + gastos + nexo + soporte files.
 *  2. Build the manifest (00_Indice_Dossier.xlsx) with ExcelJS.
 *  3. Download every document via signed URL (concurrency-limited).
 *  4. Pack everything into a JSZip and trigger the browser download.
 *  5. Resilient: failed downloads land as `_FALTANTE_<name>.txt` markers.
 */
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { supabase } from "@/integrations/supabase/client";
import {
  buildDossierFileName,
  buildDossierRootFolder,
  buildZipFileName,
  getDossierRule,
  extractExtension,
  slugifyForFile,
} from "./dossierNaming";
import type { NexoComercialRow } from "./exportNexoComercial";
import {
  fetchShipmentLandedCost,
  type ShipmentDocument,
  type ShipmentItem,
  type ShipmentGasto,
  type AllocationLine,
} from "@/hooks/useShipmentLandedCost";
import type { ProrationBase } from "@/lib/landedCostEngine";

// ── Types ──────────────────────────────────────────────────────────
export interface DossierFileEntry {
  id: string;
  source: "documento" | "soporte";
  tipo_documento: string | null;
  no_documento: string | null;
  archivo_url: string | null;
  fecha: string | null;
  /** Final ZIP path (folder/name.ext) — intended path. */
  zipPath: string;
  /** Actual path written into the ZIP (differs from zipPath on failure → _FALTANTE.txt). */
  actualZipPath?: string;
  /** Live status used by the progress dialog. */
  status: "pending" | "downloading" | "ok" | "failed";
  errorMessage?: string;
  bytes?: number;
}

export interface DossierProgress {
  totalFiles: number;
  completedFiles: number;
  currentFile: string | null;
  files: DossierFileEntry[];
  phase: "preparing" | "downloading" | "compressing" | "done" | "error";
  message?: string;
}

export type ProgressCallback = (p: DossierProgress) => void;

// ── Storage helpers ────────────────────────────────────────────────

const BUCKETS = ["documentos_kargo", "documentos_partners", "soportes_cumplimiento"] as const;
const DEFAULT_BUCKET = "documentos_kargo";
const SIGNED_URL_TTL = 7200; // 2h — generous for large dossiers
const DOWNLOAD_MAX_RETRIES = 3;
const DOWNLOAD_RETRY_BACKOFF_MS = 800;

/** Aggressively decode URI components — handles double-encoded paths (%2520 → %20 → space). */
function safeDecode(raw: string): string {
  let out = raw;
  for (let i = 0; i < 3; i++) {
    try {
      const decoded = decodeURIComponent(out);
      if (decoded === out) break;
      out = decoded;
    } catch {
      break; // malformed → stop, keep last good value
    }
  }
  return out;
}

function parseStoragePath(archivoUrl: string): { bucket: string; path: string } | null {
  if (!archivoUrl) return null;
  let raw = safeDecode(archivoUrl.trim());

  // 1. Full Supabase URL (signed or public)
  const m = raw.match(
    /\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+?)(?:\?|$)/,
  );
  if (m) {
    const bucket = m[1];
    let path = m[2].replace(/^\/+/, "");
    return { bucket, path };
  }

  // 2. Strip any leading slash
  raw = raw.replace(/^\/+/, "");

  // 3. "<bucket>/<path>" prefix form
  for (const b of BUCKETS) {
    if (raw.startsWith(`${b}/`)) {
      return { bucket: b, path: raw.slice(b.length + 1) };
    }
  }

  // 4. Bare object path → assume default bucket
  if (raw.length > 0 && !raw.startsWith("http")) {
    return { bucket: DEFAULT_BUCKET, path: raw };
  }

  return null;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function fetchBlobFromStorage(archivoUrl: string): Promise<Blob> {
  const parsed = parseStoragePath(archivoUrl);
  if (!parsed) throw new Error(`URL de archivo inválida: ${archivoUrl}`);

  let lastErr: Error | null = null;
  for (let attempt = 1; attempt <= DOWNLOAD_MAX_RETRIES; attempt++) {
    try {
      const { data, error } = await supabase.storage
        .from(parsed.bucket)
        .createSignedUrl(parsed.path, SIGNED_URL_TTL);
      if (error || !data?.signedUrl) {
        throw new Error(`No se pudo firmar URL: ${error?.message ?? "desconocido"}`);
      }
      const res = await fetch(data.signedUrl, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status} al descargar archivo`);
      return await res.blob();
    } catch (err: any) {
      lastErr = err instanceof Error ? err : new Error(String(err));
      if (attempt < DOWNLOAD_MAX_RETRIES) {
        await sleep(DOWNLOAD_RETRY_BACKOFF_MS * attempt); // 800ms, 1.6s
      }
    }
  }
  throw lastErr ?? new Error("Descarga fallida tras reintentos");
}

// ── Concurrency limiter ────────────────────────────────────────────
async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> {
  const queue = items.map((it, i) => ({ it, i }));
  const runners = Array.from({ length: Math.min(limit, queue.length) }, async () => {
    while (queue.length > 0) {
      const next = queue.shift();
      if (!next) return;
      await worker(next.it, next.i);
    }
  });
  await Promise.all(runners);
}

// ── Currency helper ────────────────────────────────────────────────
const fmtCOP = (v: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(v || 0);

// ── Manifest builder (00_Indice_Dossier.xlsx) ──────────────────────
async function buildManifestWorkbook(args: {
  numeroDo: string;
  embarque: any;
  files: DossierFileEntry[];
  finanzas: { mercancia: number; arancel: number; iva: number; ico: number; gastos: number; total: number };
  nexoRows: NexoComercialRow[];
  proveedorNombre: string | null;
  clienteNombre: string | null;
  /** Si true, los costos provienen del motor en vivo (no del freeze). */
  isProyectado: boolean;
  /** TRM Aduana usada en la conversión a COP (para nota cambiaria). */
  trmAduana: number | null;
  /** Mapa itemId → costo unitario landed proyectado (vivo) para Nexo Comercial. */
  nexoCostosVivos: Record<string, number>;
  inventarioRows: Array<{
    itemId: string;
    sku: string | null;
    descripcion: string | null;
    cantidad: number;
    precio_unitario: number;
    moneda: string | null;
    hs_code: string | null;
    aplica_tlc: boolean;
    arancel_pct: number;
    iva_pct: number;
    /** NUEVO: gastos prorrateados por base PESO (unitario). */
    gastos_peso_unit_cop: number;
    /** NUEVO: gastos prorrateados por base VALOR (+ CBM, unitario). */
    gastos_valor_unit_cop: number;
    impuestos_unit_cop: number;
    costo_nacionalizado_unitario_cop: number;
    valor_arancel_cop: number;
    valor_iva_cop: number;
    /** ICO capitalizado al ítem (0 si no aplica). */
    valor_ico_cop: number;
    no_documento: string | null;
  }>;
  /** TRACE-01: desglose granular gasto × ítem (mismo dato que la trazabilidad). */
  allocations: AllocationLine[];
  /** Catálogo enriquecido por gasto_id → metadata (concepto, moneda, TRM). */
  gastosCatalog: Record<string, {
    concepto: string;
    monto_base: number;
    monto_total: number;
    moneda: string;
    trm_pago: number | null;
    base_prorrateo: string;
  }>;
}): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Kargonode";
  wb.created = new Date();

  // ── Constantes de estilo: Estados Proyectados ──────────────────
  const AMBER_FILL = { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: "FFFEF3C7" } };
  const BLUE_FILL = { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: "FFEFF6FF" } };
  const HEADER_FILL = { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: "FF0F4C81" } };
  const NOTE_FILL = { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: "FFF3F4F6" } };
  const proySuf = args.isProyectado ? " (Proyectado)" : "";

  // Helper: nota cambiaria pre-tabla. Devuelve el nº de fila ocupada (siempre 1).
  const trmNote = args.trmAduana && args.trmAduana > 0
    ? `Valores convertidos a COP usando TRM Aduana: $${new Intl.NumberFormat("es-CO", {
        maximumFractionDigits: 2,
      }).format(args.trmAduana)} COP/USD (DIAN)${args.isProyectado ? " — ⚠ Embarque NO liquidado: cifras proyectadas por motor v2." : ""}`
    : `⚠ TRM Aduana no asignada — usando rate de mercado.${args.isProyectado ? " Embarque NO liquidado: cifras proyectadas por motor v2." : ""}`;

  function injectTrmNote(ws: ExcelJS.Worksheet, lastCol: string) {
    const r = ws.addRow([trmNote]);
    ws.mergeCells(`A${r.number}:${lastCol}${r.number}`);
    r.font = { italic: true, size: 10, color: { argb: args.isProyectado ? "FF92400E" : "FF6B7280" } };
    r.fill = args.isProyectado ? AMBER_FILL : NOTE_FILL;
    r.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
    r.height = 22;
  }

  // ── 1. Carátula ────────────────────────────────────────────────
  const cover = wb.addWorksheet("Carátula");
  cover.columns = [{ width: 36 }, { width: 56 }];
  const meta: Array<[string, string | number]> = [
    ["DOSSIER DIAN — KIT ANTISANCIONES", ""],
    ["", ""],
    ["Embarque (DO)", args.numeroDo],
    ["Cliente", args.clienteNombre ?? "—"],
    ["Proveedor", args.proveedorNombre ?? args.embarque?.proveedor ?? "—"],
    ["Estado logístico", args.embarque?.estado_logistico ?? "—"],
    ["Modo de transporte", args.embarque?.modo_transporte ?? "—"],
    ["Tracking", args.embarque?.tracking_number ?? "—"],
    ["Naviera / Línea", args.embarque?.shipping_line ?? "—"],
    ["ETA", args.embarque?.fecha_llegada_estimada ?? "—"],
    ["Llegada real", args.embarque?.fecha_llegada_real ?? "—"],
    ["TRM Aduana (COP/USD)", args.embarque?.trm_aduana ?? "—"],
    ["Liquidado", args.embarque?.liquidado ? "SÍ" : "NO"],
    ["", ""],
    ["Total archivos en dossier", args.files.length],
    ["Generado", new Date().toLocaleString("es-CO")],
  ];
  meta.forEach((row, idx) => {
    const r = cover.addRow(row);
    if (idx === 0) {
      r.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
      r.fill = HEADER_FILL;
      cover.mergeCells(`A${r.number}:B${r.number}`);
      r.height = 28;
    } else if (row[0]) {
      r.getCell(1).font = { bold: true };
    }
  });

  // Callout de advertencia si no liquidado
  if (args.isProyectado) {
    cover.addRow([""]);
    const warn = cover.addRow([
      "⚠ EMBARQUE NO LIQUIDADO — Valores proyectados por motor Landed Cost v2. No usar para declaración DIAN.",
    ]);
    cover.mergeCells(`A${warn.number}:B${warn.number}`);
    warn.font = { bold: true, color: { argb: "FF92400E" }, size: 11 };
    warn.fill = AMBER_FILL;
    warn.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    warn.height = 36;
  }

  // ── 2. Índice de Archivos (se construye al final tras las hojas financieras) ──
  // Movido: el CFO ve primero los números (Resumen, Nexo, Landed, Detalle Gastos)
  // y luego el índice de archivos físicos del ZIP.

  // ── 3. Resumen Financiero ──────────────────────────────────────
  const fin = wb.addWorksheet("Resumen Financiero");
  fin.columns = [{ width: 56 }, { width: 24 }];
  // Fila 1: Nota cambiaria (merged A:B)
  injectTrmNote(fin, "B");

  const arancelLabel = `Aranceles${proySuf}`;
  const ivaLabel = `IVA descontable (flujo de caja)${proySuf}`;
  const totalLabel = `COSTO TOTAL NACIONALIZADO (sin IVA)${proySuf}`;
  const salidaCajaLabel = `SALIDA TOTAL DE CAJA (incluye IVA)${proySuf}`;

  const icoLabel = `Impuesto al Consumo (ICO)${proySuf}`;

  const finRows: Array<[string, string | number]> = [
    ["RESUMEN FINANCIERO (COP)", ""],
    ["", ""],
    ["Valor mercancía", args.finanzas.mercancia],
    [arancelLabel, args.finanzas.arancel],
    [icoLabel, args.finanzas.ico],
    ["Gastos logísticos (capitalizables)", args.finanzas.gastos],
    ["", ""],
    [totalLabel, args.finanzas.total],
    ["", ""],
    [ivaLabel, args.finanzas.iva],
    [salidaCajaLabel, args.finanzas.total + args.finanzas.iva],
    ["", ""],
    ["Nota contable (NIC 2 / Estatuto Tributario Art. 488)", ""],
    ["El IVA de importación es descontable y NO se capitaliza al inventario. Se reporta como salida de caja para conciliación del CFO. El ICO sí forma parte del costo del inventario.", ""],
  ];
  finRows.forEach((row, i) => {
    const r = fin.addRow(row);
    if (i === 0) {
      r.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
      r.fill = HEADER_FILL;
      fin.mergeCells(`A${r.number}:B${r.number}`);
      r.height = 26;
    } else if (typeof row[1] === "number") {
      r.getCell(2).numFmt = '#,##0';
      const isTotalLanded = row[0] === totalLabel;
      const isSalidaCaja = row[0] === salidaCajaLabel;
      const isIva = row[0] === ivaLabel;
      r.getCell(1).font = { bold: isTotalLanded || isSalidaCaja };
      // Tinte ámbar a celdas financieras cuando sea proyectado
      if (args.isProyectado && (row[0] === arancelLabel || isIva || isTotalLanded || isSalidaCaja)) {
        r.getCell(1).fill = AMBER_FILL;
        r.getCell(2).fill = AMBER_FILL;
      } else if (isTotalLanded) {
        r.getCell(1).fill = BLUE_FILL;
        r.getCell(2).fill = BLUE_FILL;
      }
      if (isTotalLanded || isSalidaCaja) r.getCell(2).font = { bold: true };
    } else if (row[0]?.toString().startsWith("Nota contable")) {
      r.getCell(1).font = { bold: true, size: 10, italic: true };
      fin.mergeCells(`A${r.number}:B${r.number}`);
    } else if (row[0]?.toString().startsWith("El IVA de importación")) {
      r.getCell(1).font = { size: 9, italic: true, color: { argb: "FF6B7280" } };
      fin.mergeCells(`A${r.number}:B${r.number}`);
      r.height = 32;
      r.alignment = { wrapText: true, vertical: "top" };
    }
  });

  // ── 4. Nexo Comercial (resumen) ────────────────────────────────
  const nexo = wb.addWorksheet("Nexo Comercial");
  // Fila 1: Nota cambiaria (merged A:L = 12 cols)
  injectTrmNote(nexo, "L");

  const nexoHeaders = [
    "SKU", "Descripción", "Cantidad", "Unidad", "HS Code",
    "No. Factura", "Fecha Factura", "Proveedor",
    `Costo Unit. (COP)${proySuf}`, `Costo Total (COP)${proySuf}`,
    "No. DIM", "No. Levante",
  ];
  const nexoHeaderRow = nexo.addRow(nexoHeaders);
  nexoHeaderRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  nexoHeaderRow.fill = HEADER_FILL;
  nexoHeaderRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  nexoHeaderRow.height = 28;

  // Filtrar: solo ítems de documentos comerciales (Factura Internacional / Compras).
  // Excluye SKUs derivados de Packing List, BL, Guía Aérea, etc.
  const commercialItemIds = new Set(args.inventarioRows.map((r) => r.itemId));
  const nexoFiltered = args.nexoRows.filter((r) => commercialItemIds.has(r.item_id));

  nexoFiltered.forEach((r) => {
    const cant = r.cantidad ?? 0;
    // Tratamiento híbrido: si hay costo vivo del motor (proyectado), usarlo;
    // si no, usar el costo congelado de la vista (post-liquidación).
    const cuVivo = args.nexoCostosVivos[r.item_id];
    const cu = args.isProyectado && cuVivo != null ? cuVivo : (r.costo_unitario_cop ?? 0);
    const dataRow = nexo.addRow([
      r.sku ?? "—", r.descripcion ?? "—", cant, r.unidad_medida ?? "—",
      r.hs_code ?? "—", r.no_factura ?? "—", r.fecha_factura ?? "—",
      r.proveedor ?? "—", cu, cant * cu, r.numero_dim ?? "—", r.numero_levante ?? "—",
    ]);
    if (args.isProyectado) {
      dataRow.getCell(9).fill = AMBER_FILL;
      dataRow.getCell(10).fill = AMBER_FILL;
    }
  });
  nexo.columns = [
    { width: 16 }, { width: 38 }, { width: 10 }, { width: 10 }, { width: 14 },
    { width: 18 }, { width: 14 }, { width: 26 }, { width: 18 }, { width: 18 },
    { width: 16 }, { width: 16 },
  ];
  nexo.getColumn(9).numFmt = '"$"#,##0';
  nexo.getColumn(10).numFmt = '"$"#,##0';
  nexo.getColumn(3).numFmt = "#,##0";
  // Freeze: fila 1 (nota) + fila 2 (headers)
  nexo.views = [{ state: "frozen", ySplit: 2 }];

  // ── 5. Detalle Landed Cost ─────────────────────────────────────
  const landed = wb.addWorksheet("Detalle Landed Cost");

  // Fila 1: Nota cambiaria (merged A:O = 15 cols, con la columna ICO)
  injectTrmNote(landed, "O");

  // Fila 2: Headers manuales (no usamos `landed.columns =` para no sobrescribir
  // la fila 1 de nota). Definimos widths por separado.
  const landedHeaders = [
    "SKU",
    "Descripción",
    "No. Documento",
    "Cantidad",
    "Moneda Origen",
    "Precio Unit. (FOB origen)",
    "Arancel %",
    "IVA %",
    "Gastos por Peso Unit. (COP)",
    "Gastos por Valor Unit. (COP)",
    "Impuestos Unit. (COP)",
    `Arancel Total Item (COP)${proySuf}`,
    `IVA Total Item (COP)${proySuf}`,
    `ICO Total Item (COP)${proySuf}`,
    `Costo Landed Final (COP)${proySuf}`,
  ];
  const lh = landed.addRow(landedHeaders);
  lh.font = { bold: true, color: { argb: "FFFFFFFF" } };
  lh.fill = HEADER_FILL;
  lh.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  lh.height = 36;

  // Column widths (no headers — ya escribimos la fila 2 manualmente)
  const landedWidths = [18, 42, 18, 10, 10, 22, 11, 9, 22, 22, 20, 22, 20, 20, 26];
  landedWidths.forEach((w, i) => { landed.getColumn(i + 1).width = w; });

  args.inventarioRows.forEach((r) => {
    const row = landed.addRow([
      r.sku ?? "—",
      r.descripcion ?? "—",
      r.no_documento ?? "—",
      r.cantidad,
      r.moneda ?? "—",
      r.precio_unitario,
      r.arancel_pct / 100,
      r.iva_pct / 100,
      r.gastos_peso_unit_cop,
      r.gastos_valor_unit_cop,
      r.impuestos_unit_cop,
      r.valor_arancel_cop,
      r.valor_iva_cop,
      r.valor_ico_cop,
      r.costo_nacionalizado_unitario_cop,
    ]);
    // Highlight Landed Final column (ahora col 15)
    row.getCell(15).fill = args.isProyectado ? AMBER_FILL : BLUE_FILL;
    row.getCell(15).font = { bold: true, color: { argb: args.isProyectado ? "FF92400E" : "FF0F4C81" } };
    if (args.isProyectado) {
      row.getCell(12).fill = AMBER_FILL;
      row.getCell(13).fill = AMBER_FILL;
      row.getCell(14).fill = AMBER_FILL;
    }
    if (r.aplica_tlc) {
      row.getCell(7).font = { italic: true, color: { argb: "FF15803D" } };
    }
  });

  // Numeric formats
  landed.getColumn(4).numFmt = "#,##0.##";
  landed.getColumn(6).numFmt = "#,##0.00";
  landed.getColumn(7).numFmt = "0.00%";
  landed.getColumn(8).numFmt = "0.00%";
  for (let c = 9; c <= 15; c++) landed.getColumn(c).numFmt = '"$"#,##0';

  // Freeze: fila 1 (nota) + fila 2 (headers); columnas A-B (SKU + Desc)
  landed.views = [{ state: "frozen", ySplit: 2, xSplit: 2 }];

  // Totals row
  if (args.inventarioRows.length > 0) {
    const totalGastosPeso = args.inventarioRows.reduce(
      (a, r) => a + r.gastos_peso_unit_cop * r.cantidad,
      0,
    );
    const totalGastosValor = args.inventarioRows.reduce(
      (a, r) => a + r.gastos_valor_unit_cop * r.cantidad,
      0,
    );
    const totalRow = landed.addRow([
      "",
      "TOTAL",
      "",
      args.inventarioRows.reduce((a, r) => a + r.cantidad, 0),
      "",
      "",
      "",
      "",
      totalGastosPeso,
      totalGastosValor,
      "",
      args.finanzas.arancel,
      args.finanzas.iva,
      args.finanzas.ico,
      args.inventarioRows.reduce(
        (a, r) => a + r.costo_nacionalizado_unitario_cop * r.cantidad,
        0,
      ),
    ]);
    totalRow.font = { bold: true };
    totalRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE5E7EB" },
    };
    for (let c = 9; c <= 15; c++) totalRow.getCell(c).numFmt = '"$"#,##0';
  }

  // ── 6. Detalle Gastos por Ítem (TRACE-01 — N filas por ítem × gasto) ──
  // Esta es la hoja que el CFO necesita para auditar cada centavo.
  // Misma fuente de verdad que el modal de Trazabilidad de Costos.
  const alloc = wb.addWorksheet("Detalle Gastos por Ítem");
  injectTrmNote(alloc, "I");

  const allocHeaders = [
    "SKU",
    "Descripción Item",
    "Concepto Gasto",
    "Base Prorrateo",
    "Moneda Original",
    "Valor Original Gasto",
    "TRM Aplicada",
    "Monto COP Asignado",
    "% del Gasto",
  ];
  const ah = alloc.addRow(allocHeaders);
  ah.font = { bold: true, color: { argb: "FFFFFFFF" } };
  ah.fill = HEADER_FILL;
  ah.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  ah.height = 32;
  const allocWidths = [16, 38, 32, 26, 14, 18, 14, 20, 12];
  allocWidths.forEach((w, i) => { alloc.getColumn(i + 1).width = w; });

  // Lookup ítem → {sku, descripcion} desde inventarioRows
  const itemMeta = new Map(args.inventarioRows.map((r) => [r.itemId, r]));
  // Total asignado por gasto (para %)
  const totalPorGasto: Record<string, number> = {};
  args.allocations.forEach((a) => {
    const k = a.gastoId ?? "_";
    totalPorGasto[k] = (totalPorGasto[k] ?? 0) + a.montoCOP;
  });

  // Agrupamos por ítem para que el CFO vea cada producto con todos sus gastos juntos.
  const allocsByItem = new Map<string, AllocationLine[]>();
  args.allocations.forEach((a) => {
    if (!allocsByItem.has(a.itemId)) allocsByItem.set(a.itemId, []);
    allocsByItem.get(a.itemId)!.push(a);
  });

  for (const it of args.inventarioRows) {
    const itemAllocs = allocsByItem.get(it.itemId) ?? [];
    if (itemAllocs.length === 0) continue;
    let subtotalItem = 0;
    for (const a of itemAllocs) {
      const g = a.gastoId ? args.gastosCatalog[a.gastoId] : null;
      const concepto = g?.concepto ?? "—";
      const moneda = g?.moneda ?? "COP";
      const valorOriginal = g?.monto_base ?? 0;
      const trm = g?.trm_pago ?? (moneda === "COP" ? 1 : (args.trmAduana ?? 0));
      const baseLabel = a.baseDeclarada === "CBM"
        ? "CBM (efectivo: Valor)"
        : a.baseDeclarada;
      const totalGasto = totalPorGasto[a.gastoId ?? "_"] ?? 0;
      const pct = totalGasto > 0 ? a.montoCOP / totalGasto : 0;
      const r = alloc.addRow([
        it.sku ?? "—",
        it.descripcion ?? "—",
        concepto,
        baseLabel,
        moneda,
        valorOriginal,
        trm,
        a.montoCOP,
        pct,
      ]);
      r.getCell(6).numFmt = '#,##0.00';
      r.getCell(7).numFmt = '#,##0.00';
      r.getCell(8).numFmt = '"$"#,##0';
      r.getCell(9).numFmt = '0.00%';
      if (a.baseDeclarada === "CBM") {
        r.getCell(4).font = { italic: true, color: { argb: "FF6B7280" } };
      }
      subtotalItem += a.montoCOP;
    }
    // Subtotal por ítem
    const sub = alloc.addRow(["", `Subtotal ${it.sku ?? ""}`, "", "", "", "", "", subtotalItem, ""]);
    sub.font = { bold: true };
    sub.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } };
    sub.getCell(8).numFmt = '"$"#,##0';
  }
  alloc.views = [{ state: "frozen", ySplit: 2, xSplit: 2 }];

  // Total general (debe coincidir con args.finanzas.gastos)
  const totalAlloc = args.allocations.reduce((a, x) => a + x.montoCOP, 0);
  const totRow = alloc.addRow(["", "TOTAL GENERAL", "", "", "", "", "", totalAlloc, ""]);
  totRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  totRow.fill = HEADER_FILL;
  totRow.getCell(8).numFmt = '"$"#,##0';

  // ── 7. Índice de Archivos (después del detalle financiero) ─────
  const idx = wb.addWorksheet("Índice de Archivos");
  idx.columns = [
    { header: "Orden", key: "orden", width: 8 },
    { header: "Tipo Documento", key: "tipo", width: 32 },
    { header: "No. Documento", key: "no", width: 22 },
    { header: "Fecha", key: "fecha", width: 14 },
    { header: "Ruta en ZIP", key: "ruta", width: 60 },
    { header: "Estado", key: "estado", width: 14 },
  ];
  const headerRow = idx.getRow(1);
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  headerRow.fill = HEADER_FILL;
  headerRow.alignment = { vertical: "middle", horizontal: "center" };
  args.files.forEach((f, i) => {
    idx.addRow({
      orden: i + 1,
      tipo: f.tipo_documento ?? (f.source === "soporte" ? "Soporte de cumplimiento" : "—"),
      no: f.no_documento ?? "—",
      fecha: f.fecha ?? "—",
      ruta: f.actualZipPath ?? f.zipPath,
      estado:
        f.status === "ok" ? "OK" : f.status === "failed" ? "FALTANTE" : "Pendiente",
    });
  });
  idx.views = [{ state: "frozen", ySplit: 1 }];

  // ── 8. Notas Técnicas ──────────────────────────────────────────
  const notes = wb.addWorksheet("Notas Técnicas");
  notes.columns = [{ width: 4 }, { width: 110 }];
  const notesContent: Array<[string, string]> = [
    ["", "NOTAS TÉCNICAS — METODOLOGÍA DE CÁLCULO LANDED COST"],
    ["", ""],
    ["1.", "IVA descontable (NIC 2 / Estatuto Tributario Art. 488)"],
    ["", "El IVA de importación NO se capitaliza al costo del inventario. Se reporta como salida de caja en el resumen financiero para conciliación CFO, separado del Costo Total Nacionalizado."],
    ["", ""],
    ["2.", "Prorrateo de gastos — Algoritmo Largest Remainder"],
    ["", "Cada gasto se asigna a los ítems en proporción a su base efectiva (Peso bruto kg o Valor FOB en COP). Los céntimos sobrantes por redondeo se distribuyen al ítem con el mayor residuo, garantizando matemáticamente que la suma de las asignaciones individuales sea idéntica al total del gasto. Drift máximo: $0 COP."],
    ["", ""],
    ["3.", "Base CBM (Volumen)"],
    ["", "Los gastos marcados con base 'CBM' se prorratean efectivamente por Valor FOB. Esto se debe a que el motor v2 unifica el pool de Volumen y Valor en una única bolsa. La etiqueta original 'CBM' se preserva en la columna 'Base Prorrateo' para trazabilidad declarativa."],
    ["", ""],
    ["4.", "Documentos no comerciales (Packing List, BL, Guía Aérea, etc.)"],
    ["", "Estos documentos forman parte del expediente DIAN (incluidos en el ZIP y en el Índice de Archivos), pero NO aportan valor al desglose financiero del Landed Cost. El 'Valor Mercancía' del Resumen Financiero es el FOB real, derivado únicamente de Facturas Internacionales y Facturas de Compras."],
    ["", ""],
    ["5.", "Garantía de cuadre al céntimo"],
    ["", "La suma de la columna 'Monto COP Asignado' en la hoja 'Detalle Gastos por Ítem' coincide exactamente con la suma de los gastos capitalizables (monto_base, sin IVA). Verificación recomendada: TOTAL GENERAL al pie de la hoja debe igualar el campo 'Gastos logísticos' del Resumen Financiero."],
    ["", ""],
    ["6.", "Fuente única de verdad"],
    ["", "Tanto este dossier como la pantalla de 'Trazabilidad de Costos' en la plataforma se alimentan del mismo RPC PostgreSQL: _calculate_landed_cost_internal (motor v2). Cualquier discrepancia entre ambas vistas constituye un bug — reportar a soporte adjuntando este ZIP."],
    ["", ""],
    ["7.", "TRM aplicada"],
    ["", `Las conversiones a COP usan la TRM Aduana del embarque cuando está definida (oficial DIM); en su ausencia, el motor utiliza la TRM histórica de mercado por moneda y el dossier se marca como 'Proyectado'. La TRM por gasto individual respeta la 'TRM de Pago' capturada en el momento de la liquidación de cada factura.`],
  ];
  notesContent.forEach((row, i) => {
    const r = notes.addRow(row);
    if (i === 0) {
      r.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
      r.fill = HEADER_FILL;
      notes.mergeCells(`A${r.number}:B${r.number}`);
      r.height = 28;
      r.alignment = { vertical: "middle", horizontal: "center" };
    } else if (row[0]) {
      // Numbered header line
      r.font = { bold: true, size: 11, color: { argb: "FF0F4C81" } };
    } else if (row[1]) {
      // Body paragraph
      r.getCell(2).font = { size: 10, color: { argb: "FF374151" } };
      r.getCell(2).alignment = { wrapText: true, vertical: "top" };
      r.height = 48;
    }
  });

  return await wb.xlsx.writeBuffer();
}

// ── Main entrypoint ────────────────────────────────────────────────
export async function generateDossierZip(
  embarqueId: string,
  numeroDo: string,
  onProgress?: ProgressCallback,
): Promise<void> {
  const emit = (p: Partial<DossierProgress> & { files: DossierFileEntry[] }) => {
    onProgress?.({
      totalFiles: p.files.length,
      completedFiles: p.files.filter((f) => f.status === "ok" || f.status === "failed").length,
      currentFile: p.currentFile ?? null,
      files: p.files,
      phase: p.phase ?? "preparing",
      message: p.message,
    });
  };

  // ── 1. Fetch all data in parallel ───────────────────────────
  emit({ files: [], phase: "preparing", message: "Recopilando documentos del embarque…" });

  const [embRes, docsRes, gastosRes, soporteRes, nexoRes] = await Promise.all([
    supabase.from("embarques").select("*, clientes(nombre), proveedores(nombre)").eq("id", embarqueId).maybeSingle(),
    supabase
      .from("registro_documentos")
      .select("id, tipo_documento, no_documento, fecha, archivo_url, total_factura, moneda, created_at, costo_flete, seguro, otros_cargos, impuestos")
      .eq("embarque_id", embarqueId)
      .is("deleted_at", null)
      .order("created_at", { ascending: true }),
    supabase
      .from("gastos_embarque")
      .select("id, concepto, monto, monto_base, moneda, trm_pago, base_prorrateo, aplica_costeo_inventario")
      .eq("embarque_id", embarqueId)
      .is("deleted_at", null),
    supabase
      .from("archivos_soporte_cargados")
      .select("id, archivo_url, nombre_archivo, codigo_partida, fecha_vencimiento, descripcion_libre, documento_id")
      .in(
        "documento_id",
        // Will be filled after we know docs; use placeholder for now.
        ["00000000-0000-0000-0000-000000000000"],
      ),
    (supabase as any).from("vista_nexo_comercial").select("*").eq("embarque_id", embarqueId),
  ]);

  if (embRes.error) throw new Error(`Embarque: ${embRes.error.message}`);
  if (docsRes.error) throw new Error(`Documentos: ${docsRes.error.message}`);
  if (gastosRes.error) throw new Error(`Gastos: ${gastosRes.error.message}`);
  if (nexoRes.error) throw new Error(`Nexo Comercial: ${nexoRes.error.message}`);

  const embarque = embRes.data;
  const docs = docsRes.data ?? [];
  const gastos = gastosRes.data ?? [];
  const nexoRows = (nexoRes.data ?? []) as NexoComercialRow[];

  // ── 1b. Re-fetch soportes with real doc IDs ─────────────────
  const docIds = docs.map((d) => d.id);
  let soportes: any[] = [];
  if (docIds.length > 0) {
    const { data, error } = await supabase
      .from("archivos_soporte_cargados")
      .select("id, archivo_url, nombre_archivo, codigo_partida, fecha_vencimiento, descripcion_libre, documento_id")
      .in("documento_id", docIds);
    if (error) throw new Error(`Soportes: ${error.message}`);
    soportes = data ?? [];
  }

  // ── 2. Build entry list (sequential numbering 01, 02, 03…) ─────
  // Sort first by DIAN rule prefix (logical order), then renumber sequentially
  // so the user sees 01, 02, 03, 04… without gaps.
  const orderedDocs = docs
    .filter((d) => !!d.archivo_url)
    .map((d) => ({ d, rule: getDossierRule(d.tipo_documento) }))
    .sort((a, b) => a.rule.prefix.localeCompare(b.rule.prefix));

  const docEntries: DossierFileEntry[] = orderedDocs.map(({ d, rule }, idx) => {
    const orderNumber = (idx + 1).toString().padStart(2, "0");
    const ext = extractExtension(d.archivo_url);
    const slugDoc = slugifyForFile(d.no_documento);
    const docPart = slugDoc ? `_${slugDoc}` : "";
    const fileName = `${orderNumber}_${rule.baseName}${docPart}.${ext}`;
    const folderPath = rule.folder ? `${rule.folder}/` : "";
    return {
      id: d.id,
      source: "documento" as const,
      tipo_documento: d.tipo_documento,
      no_documento: d.no_documento,
      archivo_url: d.archivo_url,
      fecha: d.fecha,
      zipPath: `${folderPath}${fileName}`,
      status: "pending" as const,
    };
  });

  let soporteSeq = 0;
  const soporteEntries: DossierFileEntry[] = soportes
    .filter((s) => !!s.archivo_url)
    .map((s) => {
      soporteSeq += 1;
      const ext = s.archivo_url.split(".").pop()?.toLowerCase() || "pdf";
      const slug = (s.nombre_archivo || s.codigo_partida || `soporte_${soporteSeq}`)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .slice(0, 50);
      const fileName = `${String(soporteSeq).padStart(2, "0")}_${slug}.${ext}`;
      return {
        id: s.id,
        source: "soporte" as const,
        tipo_documento: "Soporte de Cumplimiento",
        no_documento: s.codigo_partida ?? s.nombre_archivo ?? "—",
        archivo_url: s.archivo_url,
        fecha: s.fecha_vencimiento ?? null,
        zipPath: `Soportes_Cumplimiento/${fileName}`,
        status: "pending" as const,
      };
    });

  // docEntries already sequentially numbered (01, 02, 03…) — no re-sort needed

  const allFiles: DossierFileEntry[] = [...docEntries, ...soporteEntries];

  emit({ files: allFiles, phase: "downloading", message: "Descargando archivos…" });

  // ── 3. Compute financial summary — UNIFICADO con la trazabilidad ─
  // Fuente única de verdad: RPC `calculate_landed_cost_preview` (TRACE-01).
  // Esto garantiza que el dossier muestre EXACTAMENTE los mismos números que
  // ItemTraceabilityModal — sin drift de redondeo ni doble-conteo.
  const trm = Number(embarque?.trm_aduana ?? 0);
  const isProyectado = !embarque?.liquidado;

  // Solo Factura Internacional / Factura de Compras aportan FOB y líneas de inventario
  // al desglose de Landed Cost. Packing List / BL siguen en el ZIP+Índice (evidencia DIAN)
  // pero NO inflan el resumen financiero.
  const COMMERCIAL_TYPES = new Set(["Factura Internacional", "Factura de Compras"]);
  const docsComerciales = docs.filter((d) => COMMERCIAL_TYPES.has(d.tipo_documento ?? ""));
  const docsComercialesIds = new Set(docsComerciales.map((d) => d.id));

  let totalArancel = 0;
  let totalIva = 0;
  let totalIco = 0;
  let totalLanded = 0;
  let totalFOB = 0;
  let totalGastosAsignados = 0;
  let inventarioRows: Array<{
    itemId: string;
    sku: string | null;
    descripcion: string | null;
    cantidad: number;
    precio_unitario: number;
    moneda: string | null;
    hs_code: string | null;
    aplica_tlc: boolean;
    arancel_pct: number;
    iva_pct: number;
    gastos_peso_unit_cop: number;
    gastos_valor_unit_cop: number;
    impuestos_unit_cop: number;
    costo_nacionalizado_unitario_cop: number;
    valor_arancel_cop: number;
    valor_iva_cop: number;
    valor_ico_cop: number;
    no_documento: string | null;
  }> = [];
  const nexoCostosVivos: Record<string, number> = {};
  let allocations: AllocationLine[] = [];
  // Catálogo enriquecido de gastos para la hoja de detalle (concepto, moneda, TRM, etc.)
  const gastosCatalog: Record<string, {
    concepto: string;
    monto_base: number;
    monto_total: number;
    moneda: string;
    trm_pago: number | null;
    base_prorrateo: string;
  }> = {};
  gastos.forEach((g: any) => {
    gastosCatalog[g.id] = {
      concepto: g.concepto ?? "—",
      monto_base: Number(g.monto_base ?? 0),
      monto_total: Number(g.monto ?? 0),
      moneda: (g.moneda ?? "COP").toUpperCase(),
      trm_pago: g.trm_pago != null ? Number(g.trm_pago) : null,
      base_prorrateo: g.base_prorrateo ?? "Valor",
    };
  });

  if (docsComercialesIds.size > 0) {
    const { data: inv } = await supabase
      .from("base_inventario")
      .select(
        "id, sku, descripcion, cantidad, precio_unitario, sub_total, peso_bruto_kg, hs_code, aplica_tlc, beneficio_aduanero, iva_override, valor_arancel_cop, valor_iva_cop, costo_nacionalizado_unitario_cop, no_documento, documento_id",
      )
      .in("documento_id", Array.from(docsComercialesIds))
      .is("deleted_at", null);

    // Filtra líneas residuales sin precio (p.ej. fragmentos de Packing pegados a Factura).
    const invList = (inv ?? []).filter((i: any) => Number(i.precio_unitario ?? 0) > 0);

    if (invList.length > 0) {
      const docMoneda: Record<string, string> = {};
      docs.forEach((d) => { docMoneda[d.id] = (d.moneda ?? "USD").toUpperCase(); });

      // Llamada al RPC — misma fuente de verdad que la trazabilidad.
      const orgId = (embarque as any)?.org_id as string | undefined;
      if (!orgId) throw new Error("Embarque sin org_id; no se puede calcular landed cost.");

      const engineDocs: ShipmentDocument[] = docsComerciales.map((d: any) => ({
        id: d.id,
        moneda: d.moneda,
        costo_flete: d.costo_flete,
        seguro: d.seguro,
        otros_cargos: d.otros_cargos,
        impuestos: d.impuestos,
      }));
      const engineItems: ShipmentItem[] = invList.map((i: any) => ({
        id: i.id,
        documento_id: i.documento_id,
        sku: i.sku,
        descripcion: i.descripcion,
        precio_unitario: i.precio_unitario,
        cantidad: i.cantidad,
        sub_total: i.sub_total,
        peso_bruto_kg: i.peso_bruto_kg,
        hs_code: i.hs_code,
        aplica_tlc: i.aplica_tlc,
        beneficio_aduanero: i.beneficio_aduanero,
        iva_override: i.iva_override,
      }));
      const engineGastos: ShipmentGasto[] = (gastos as any[]).map((g) => ({
        id: g.id,
        concepto: g.concepto ?? "",
        monto: Number(g.monto ?? 0),
        monto_base: g.monto_base != null ? Number(g.monto_base) : null,
        moneda: g.moneda ?? "COP",
        base_prorrateo: (g.base_prorrateo ?? "Valor") as ProrationBase,
        trm_pago: g.trm_pago,
        aplica_costeo_inventario: g.aplica_costeo_inventario ?? true,
      }));

      // PARIDAD UI/Dossier: misma lógica de resolución de beneficio aduanero
      // que `EmbarqueDetailModal`. Si el ítem tiene `beneficio_aduanero` explícito
      // (incluyendo 'GENERAL'), gana sobre el flag legacy `aplica_tlc`. Sin esto,
      // un ítem con beneficio='GENERAL' + aplica_tlc=true sería tratado como
      // TLC_PURO por el adapter y daría arancel 0% (divergiendo de la UI).
      const beneficioByItemId: Record<string, "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA"> = {};
      invList.forEach((i: any) => {
        const explicit = i.beneficio_aduanero as
          | "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA" | null | undefined;
        if (explicit) {
          beneficioByItemId[i.id] = explicit;
        } else {
          beneficioByItemId[i.id] = i.aplica_tlc ? "TLC_PURO" : "GENERAL";
        }
      });

      // 🔍 DIAG PAYLOAD: comparar exactamente lo que el dossier manda al mismo RPC que la UI.
      try {
        // eslint-disable-next-line no-console
        console.log("🔍 PAYLOAD DOSSIER:", JSON.stringify({
          orgId,
          documents: engineDocs.map((d) => ({ id: d.id, moneda: d.moneda })),
          items: engineItems.map((i) => ({
            id: i.id,
            hs_code: (i as { hs_code?: string | null }).hs_code,
            aplica_tlc: i.aplica_tlc,
            beneficio_aduanero: (i as { beneficio_aduanero?: string | null }).beneficio_aduanero,
          })),
          gastos: engineGastos.map((g) => ({ id: g.id, monto: g.monto, moneda: g.moneda })),
          trmAduana: trm > 0 ? trm : null,
          beneficioByItemId,
        }, null, 2));
      } catch (e) {
        console.warn("[DossierExport] payload diag log failed", e);
      }

      const lc = await fetchShipmentLandedCost({
        orgId,
        documents: engineDocs,
        items: engineItems,
        gastos: engineGastos,
        trmAduana: trm > 0 ? trm : null,
        beneficioByItemId,
      });

      allocations = lc.allocations;
      const itemMap = new Map(lc.items.map((r) => [r.itemId, r]));

      // 🔬 DIAG ARANCEL: trazar pipeline RPC → adapter → dossier
      try {
        // eslint-disable-next-line no-console
        console.groupCollapsed(`🔬 [DossierExport] Arancel pipeline — ${numeroDo} (isProyectado=${isProyectado})`);
        console.log("trmAduana:", trm);
        console.table(
          lc.items.map((r) => {
            const item = (invList as any[]).find((i: any) => i.id === r.itemId);
            return {
              itemId: r.itemId,
              hs_code: item?.hs_code ?? "—",
              sku: item?.sku ?? "—",
              "adapter.arancelPctEfectivo": r.arancelPctEfectivo,
              "adapter.ivaPctEfectivo": r.ivaPctEfectivo,
              "adapter.arancelItemCOP": r.arancelItemCOP,
              "adapter.cifItemCOP": r.cifItemCOP,
            };
          }),
        );
        console.groupEnd();
      } catch (e) {
        console.warn("[DossierExport] diag log failed", e);
      }

      // FUENTE ÚNICA DE VERDAD: el RPC ya resuelve arancel/IVA % efectivos
      // (con normalización de hs_code, cascada de prefijos y aplicación de
      // beneficios aduaneros). Para snapshots liquidados, derivamos el % desde
      // los montos congelados en base_inventario para mantener consistencia
      // con el momento del cierre.

      // FOB en COP por ítem (sub_total × TRM aplicada por moneda doc).
      // Lo dejamos derivar del CIF − allocations para no duplicar lógica de TRM.
      // (CIF = FOB + allocPeso + allocValor en el motor v2.)
      inventarioRows = invList.map((i: any) => {
        const eng = itemMap.get(i.id);
        const cantidad = Number(i.cantidad ?? 0);
        const allocPeso = eng?.gastosPesoAsignados ?? 0;
        const allocValor = eng?.gastosValorAsignados ?? 0;
        const cifCop = eng?.cifItemCOP ?? 0;
        const fobItemCop = cifCop - allocPeso - allocValor;
        totalFOB += fobItemCop;

        const arancelCop = isProyectado ? (eng?.arancelItemCOP ?? 0) : Number(i.valor_arancel_cop ?? 0);
        const ivaCop = isProyectado ? (eng?.ivaItemCOP ?? 0) : Number(i.valor_iva_cop ?? 0);
        // ICO sólo disponible en motor vivo. Snapshot legacy NO persiste ICO.
        const icoCop = isProyectado ? (eng?.icoCOP ?? 0) : 0;
        const landedUnit = isProyectado
          ? (eng?.landedCostUnitarioCOP ?? 0)
          : Number(i.costo_nacionalizado_unitario_cop ?? 0);

        if (isProyectado && eng) nexoCostosVivos[i.id] = landedUnit;

        // Arancel/IVA %: para PROYECTADO viene del RPC (fuente única); para
        // LIQUIDADO se deriva del snapshot congelado (cif≈landed-arancel).
        let arancelPct: number;
        let ivaPct: number;
        if (isProyectado) {
          arancelPct = Number(eng?.arancelPctEfectivo ?? 0);
          ivaPct = Number(eng?.ivaPctEfectivo ?? (i.iva_override ?? 19));
        } else {
          const cifApprox = Math.max(0, (Number(i.costo_nacionalizado_unitario_cop ?? 0) * cantidad) - arancelCop);
          arancelPct = cifApprox > 0 ? (arancelCop / cifApprox) * 100 : 0;
          const baseGravable = cifApprox + arancelCop;
          ivaPct = baseGravable > 0 ? (ivaCop / baseGravable) * 100 : Number(i.iva_override ?? 19);
        }


        return {
          itemId: i.id,
          sku: i.sku,
          descripcion: i.descripcion,
          cantidad,
          precio_unitario: Number(i.precio_unitario ?? 0),
          moneda: docMoneda[i.documento_id] ?? "USD",
          hs_code: i.hs_code,
          aplica_tlc: !!i.aplica_tlc,
          valor_arancel_cop: arancelCop,
          valor_iva_cop: ivaCop,
          valor_ico_cop: icoCop,
          costo_nacionalizado_unitario_cop: landedUnit,
          no_documento: i.no_documento,
          arancel_pct: arancelPct,
          iva_pct: ivaPct,
          gastos_peso_unit_cop: cantidad > 0 ? allocPeso / cantidad : 0,
          gastos_valor_unit_cop: cantidad > 0 ? allocValor / cantidad : 0,
          impuestos_unit_cop: cantidad > 0 ? (arancelCop + ivaCop + icoCop) / cantidad : 0,
        };
      });

      // 🔬 DIAG: valores FINALES escritos al Excel del dossier
      try {
        // eslint-disable-next-line no-console
        console.groupCollapsed(`🔬 [DossierExport] Valores finales en filas Excel — ${numeroDo}`);
        console.table(
          inventarioRows.map((r) => ({
            sku: r.sku,
            hs_code: r.hs_code,
            "dossier.arancel_pct": r.arancel_pct,
            "dossier.iva_pct": r.iva_pct,
            "dossier.valor_arancel_cop": r.valor_arancel_cop,
          })),
        );
        console.groupEnd();
      } catch {}

      totalArancel = isProyectado
        ? lc.items.reduce((a, r) => a + r.arancelItemCOP, 0)
        : invList.reduce((a: number, i: any) => a + Number(i.valor_arancel_cop ?? 0), 0);
      totalIva = isProyectado
        ? lc.items.reduce((a, r) => a + r.ivaItemCOP, 0)
        : invList.reduce((a: number, i: any) => a + Number(i.valor_iva_cop ?? 0), 0);
      totalIco = isProyectado
        ? lc.items.reduce((a, r) => a + ((r as any).icoCOP ?? 0), 0)
        : 0;
      totalLanded = isProyectado
        ? lc.totalLandedCOP
        : invList.reduce(
            (a: number, i: any) => a + Number(i.costo_nacionalizado_unitario_cop ?? 0) * Number(i.cantidad ?? 0),
            0,
          );
      totalGastosAsignados = allocations.reduce((a, x) => a + x.montoCOP, 0);
    }
  }

  const finanzas = {
    mercancia: totalFOB,             // FOB real (no total_factura espurio)
    arancel: totalArancel,
    iva: totalIva,
    ico: totalIco,
    gastos: totalGastosAsignados,    // Σ allocations — sin doble-conteo
    total: totalLanded,              // === ItemTraceabilityModal al céntimo
  };


  // ── 4. Download all files (concurrency 4) ───────────────────
  const zip = new JSZip();
  const root = buildDossierRootFolder(numeroDo);
  const rootFolder = zip.folder(root)!;

  await runWithConcurrency(allFiles, 4, async (entry) => {
    entry.status = "downloading";
    emit({ files: allFiles, phase: "downloading", currentFile: entry.zipPath });
    try {
      const blob = await fetchBlobFromStorage(entry.archivo_url!);
      const buf = await blob.arrayBuffer();
      rootFolder.file(entry.zipPath, buf);
      entry.status = "ok";
      entry.actualZipPath = entry.zipPath;
      entry.bytes = buf.byteLength;
    } catch (err: any) {
      entry.status = "failed";
      entry.errorMessage = err?.message ?? "Error desconocido";
      const txtName = entry.zipPath.replace(/\.[^./]+$/, "") + "_FALTANTE.txt";
      rootFolder.file(
        txtName,
        `Este archivo no pudo ser descargado durante la generación del dossier.\n\n` +
          `Tipo: ${entry.tipo_documento}\n` +
          `No. Documento: ${entry.no_documento}\n` +
          `URL original: ${entry.archivo_url}\n` +
          `Razón: ${entry.errorMessage}\n` +
          `Generado: ${new Date().toISOString()}\n`,
      );
      entry.actualZipPath = txtName;
    }
    emit({ files: allFiles, phase: "downloading", currentFile: entry.zipPath });
  });

  // ── 5. Build manifest XLSX (after all statuses are final) ───
  emit({ files: allFiles, phase: "compressing", message: "Generando índice y comprimiendo…" });
  const manifestBuf = await buildManifestWorkbook({
    numeroDo,
    embarque,
    files: allFiles,
    finanzas,
    nexoRows,
    proveedorNombre: (embarque as any)?.proveedores?.nombre ?? null,
    clienteNombre: (embarque as any)?.clientes?.nombre ?? null,
    isProyectado,
    trmAduana: trm > 0 ? trm : null,
    nexoCostosVivos,
    inventarioRows,
    allocations,
    gastosCatalog,
  });
  rootFolder.file("00_Indice_Dossier.xlsx", manifestBuf);

  // ── 5b. Legal HMAC signature (DIAN-03) ──────────────────────
  // Llamamos a la edge function `sign-dossier`, que hashea cada archivo
  // del expediente activo en Storage y devuelve un manifest canónico +
  // firma HMAC-SHA256. Inyectamos ambos en la raíz del ZIP para que el
  // expediente quede legalmente verificable con `verify-dossier`.
  emit({
    files: allFiles,
    phase: "compressing",
    message: "Firmando criptográficamente el expediente…",
  });
  try {
    const { data: signed, error: signErr } = await supabase.functions.invoke(
      "sign-dossier",
      { body: { embarque_id: embarqueId } },
    );
    if (signErr) throw signErr;
    if (!signed?.manifest || !signed?.signature) {
      throw new Error("Respuesta de firma incompleta (manifest/signature ausente)");
    }
    rootFolder.file("manifest.json", JSON.stringify(signed.manifest, null, 2));
    rootFolder.file(
      "SIGNATURE.txt",
      [
        `ALGORITHM: ${signed.algorithm ?? "HMAC-SHA256"}`,
        `KEY_ID: ${signed.key_id ?? "dossier-hmac-v1"}`,
        `SIGNATURE: ${signed.signature}`,
        `DOSSIER_ROOT_HASH: ${signed.dossier_root_hash}`,
        `SIGNATURE_ID: ${signed.signature_id}`,
        `SIGNED_AT: ${signed.signed_at}`,
        ``,
        `Para verificar la integridad de este expediente invoca la edge`,
        `function "verify-dossier" con: { "signature_id": "${signed.signature_id}" }`,
      ].join("\n"),
    );
  } catch (err: any) {
    // Sin firma legal → no entregamos el ZIP. Mejor abortar que entregar
    // un dossier que pretende ser auditable pero no lo es.
    throw new Error(
      `No se pudo firmar legalmente el expediente: ${err?.message ?? "error desconocido"}. ` +
        `El ZIP no fue generado.`,
    );
  }


  // ── 6. Compress and download ────────────────────────────────
  const blob = await zip.generateAsync(
    { type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } },
    (metadata) => {
      emit({
        files: allFiles,
        phase: "compressing",
        message: `Comprimiendo… ${Math.round(metadata.percent)}%`,
      });
    },
  );

  saveAs(blob, buildZipFileName(numeroDo));
  emit({ files: allFiles, phase: "done", message: `Dossier generado (${fmtCOP(finanzas.total)})` });
}
